import { useEffect, useRef, useState } from 'react'

import type { Reaction } from '../../api/types'
import { SmileyIcon } from '../../components/Icons'
import { useOnline } from '../../hooks/useOutbox'

/**
 * Was zur Auswahl steht — dieselbe Liste wie im Server.
 *
 * Eine feste Auswahl statt einer vollständigen Emoji-Tastatur: sie passt in
 * eine Zeile, ist mit dem Daumen zu treffen, und in der Datenbank landet
 * nichts, was niemand erwartet hat.
 */
export const EMOJI = ['❤️', '👍', '😍', '😂', '😮', '🎉', '👏', '🙏']

/**
 * Die Reaktionen zu einem Ziel — einer Nachricht oder einem Foto.
 *
 * Angezeigt wird, was der Server geschickt hat. Er entscheidet auch, wessen
 * Namen dabeistehen: ein Empfänger sieht die Reaktionen der anderen nicht,
 * sonst verriete ein Beitrag mit drei Herzen, wie viele mitlesen.
 */
export function Reactions({
  reactions,
  onPick,
  busy,
  compact,
}: {
  reactions: Reaction[]
  onPick: (emoji: string) => void
  busy?: boolean
  /** Über einem Foto: kleiner und auf dunklem Grund. */
  compact?: boolean
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  // **Ohne Netz abgeschaltet, nicht stumm.** Ein Emoji ist ein Schreibzugriff
  // wie jeder andere; vorher passierte beim Antippen schlicht nichts, ohne
  // ein Wort -- die einzige Stelle der Anwendung, die geschwiegen hat.
  const online = useOnline()
  const gesperrt = busy || !online
  const hinweis = online ? undefined : 'Ohne Netz geht das gerade nicht.'

  useEffect(() => {
    if (!open) return
    function outside(event: MouseEvent) {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    // Nächster Tick: der Klick, der geöffnet hat, soll nicht gleich schließen.
    const id = setTimeout(() => document.addEventListener('click', outside), 0)
    return () => {
      clearTimeout(id)
      document.removeEventListener('click', outside)
    }
  }, [open])

  return (
    <div
      ref={box}
      className={compact ? 'reactions reactions--compact' : 'reactions'}
      onClick={(event) => event.stopPropagation()}
    >
      {reactions.map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          className={reaction.mine ? 'reaction reaction--mine' : 'reaction'}
          disabled={gesperrt}
          onClick={() => onPick(reaction.emoji)}
          // Die Namen kommen vom Server -- steht dort nichts, steht hier auch
          // nichts.
          title={hinweis ?? reaction.who.join(', ')}
        >
          <span aria-hidden="true">{reaction.emoji}</span>
          {reaction.count > 1 && <span className="reaction__count">{reaction.count}</span>}
          <span className="visually-hidden">
            {reaction.who.length > 0 ? reaction.who.join(', ') : `${reaction.count}`}
          </span>
        </button>
      ))}

      <button
        type="button"
        className="reaction reaction--add"
        disabled={gesperrt}
        onClick={() => setOpen((value) => !value)}
        aria-label="Mit einem Emoji reagieren"
        aria-expanded={open}
        title={hinweis ?? 'Reagieren'}
      >
        <SmileyIcon size={compact ? 14 : 15} />
      </button>

      {open && (
        <div className="picker" role="menu">
          {EMOJI.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="picker__item"
              role="menuitem"
              onClick={() => {
                onPick(emoji)
                setOpen(false)
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
